import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useEffect, useState } from "react";
import Scanner from "components/Scanner";
import { getScanText, playCorrectSound, playIncorrectSound, selectAndFocus, TRACKING_MAX_LENGTH } from "utils";
import { ClientGetRouteList, ClientGetStationDetailById, ClientStorePackage, ClientStoreTrackingLog } from "api/client";
import { customer } from "localStorage";
import Swal from "sweetalert2";

const ClientBooking = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const [routeId, setRouteId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [description, setDescription] = useState("");
  const [remark, setRemark] = useState("");
  const [routeList, setRouteList] = useState([]);
  const _customer = customer();

  useEffect(() => {
    ClientGetRouteList().then((res) => {
      if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
        setRouteList(res.data);
        if (res.data.some((route) => route.id === _customer.default_route)) {
          setRouteId(_customer.default_route);
        }
      }
    });
  }, []);

  const clearForm = () => {
    setTracking("");
    setOrderId("");
    setDescription("");
    setRemark("");
    setOpen(false);
  }

  const formSubmitedHandler = (e) => {
    e.preventDefault();
    if (!tracking || tracking.trim() === "") {
      alert("Please enter tracking number");
      selectAndFocus("tracking");
      return;
    }
    if (!routeId || routeId.trim() === "") {
      alert("Please select pickup station");
      selectAndFocus("route");
      return;
    }

    ClientStorePackage(
      tracking,routeId,orderId,description,remark
    ).then((res) => {
      console.log("🚀 ~ ClientStorePackage ~ res:", res);
      if (res.status === 201) {
        let data = res.result;
        clearForm();
        playCorrectSound();
        let account = _customer.name;
        ClientGetStationDetailById("STD").then((res) => {
          if (res.status === 200) {
            ClientStoreTrackingLog(
              data._id,
              data.tracking,
              res.data.name,
              res.data.message,
              data.remark,
              account
            ).then((res) => {
              // console.log(res);
              if (res.status === 200) {
                console.log(`Trackinglog has ben saved for started station.`);
              } else {
                Swal.fire({
                  title: "Error!",
                  icon: "error",
                  text: `Trackinglog saving failed, please check with developer!`,
                  showConfirmButton: true,
                });
              }
            });
          }
        });
        Swal.fire({
          title: "Created!",
          icon: "success",
          text: `${tracking} has been created`,
          timer: 1500,
        });
      } else if (res.status === 400) {
        playIncorrectSound();
        Swal.fire({
          title: "Error!",
          icon: "error",
          text: `Status ${res.status} - ${res.data.message}`,
          showConfirmButton: true,
        });
      } else {
        playIncorrectSound();
        Swal.fire({
          icon: "error",
          text: res.message,
          showConfirmButton: true,
        });
      }
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="600" color="secondary" sx={{ mb: 1 }}>
          Booking
        </Typography>
        <Typography variant="body1" color="text.secondary">
          ປ້ອນຂໍ້ມູນ Tracking number ແລະຂໍ້ມູນພັດສະດຸຂອງທ່ານ
        </Typography>
      </Box>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: `1px solid ${theme.palette.divider}`,
          maxWidth: 820,
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <form onSubmit={formSubmitedHandler}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Pickup station *</InputLabel>
                  <Select
                    id="route"
                    value={!routeId ? "" : routeId}
                    label="Pickup station"
                    onChange={(event) => setRouteId(event.target.value)}
                    required
                    sx={{ borderRadius: 2 }}
                  >
                    {routeList &&
                      routeList.map(({ id, name, nameLA }) => {
                        return (
                          <MenuItem key={id} value={id}>
                            {nameLA} / {name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                  <FormHelperText id="route-helper-text">
                    ເລືອກສາຂາຮັບສິນຄ້າ
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tracking Number *</InputLabel>
                  <OutlinedInput
                    label="Tracking Number *"
                    id="tracking"
                    value={tracking}
                    required={true}
                    inputProps={{ maxLength: TRACKING_MAX_LENGTH }}
                    onChange={(e) => setTracking(getScanText(e.target.value))}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setTracking(getScanText(tracking));
                        document.getElementById("description").select();
                        document.getElementById("description").focus();
                        e.preventDefault();
                      }
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                  <FormHelperText id="tracking-helper-text">
                    ເລກລະຫັດພັດສະດຸ
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Order ID</InputLabel>
                  <OutlinedInput
                    type="text"
                    label="Order ID"
                    value={orderId}
                    id="orderId"
                    onChange={(e) => setOrderId(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  />
                  <FormHelperText id="orderId-helper-text">
                    ເລກລະຫັດຄຳສັ່ງຊື້
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    multiline
                    maxRows={5}
                    minRows={3}
                    label="Description"
                    value={description}
                    id="description"
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <FormHelperText id="description-helper-text">
                    ລາຍລະອຽດພັດສະດຸ
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    multiline
                    maxRows={5}
                    label="Remark"
                    value={remark}
                    id="remark"
                    onChange={(e) => setRemark(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <FormHelperText id="remark-helper-text">
                    ໝາຍເຫດ
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  size="large"
                  fullWidth
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Card>

      <Scanner
        open={open}
        setOpen={setOpen}
        closeAfterResult={true}
        onDecode={(result) => {
          if (result) {
            setTracking(() => getScanText(result));
            document.getElementById("description").select();
            document.getElementById("description").focus();
            setOpen(false);
          }
        }}
      />
    </Box>
  );
};

export default ClientBooking;
